import React, { useEffect, useState } from 'react';
import {
  HStack,
  IconButton,
  Tooltip,
  useColorMode,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
} from '@chakra-ui/react';
import { FiSun, FiMoon, FiMonitor, FiCheck } from 'react-icons/fi';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeToggleProps {
  variant?: 'segmented' | 'menu' | 'icon';
  size?: 'sm' | 'md';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  variant = 'segmented',
  size = 'sm',
}) => {
  const { colorMode, setColorMode } = useColorMode();
  const [preference, setPreference] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('theme-preference') as ThemeMode;
    return saved === 'light' || saved === 'dark' || saved === 'system' ? saved : 'system';
  });

  // Apply theme according to preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const applyTheme = (pref: ThemeMode) => {
      if (pref === 'system') {
        setColorMode(mediaQuery.matches ? 'dark' : 'light');
      } else {
        setColorMode(pref);
      }
    };

    applyTheme(preference);

    // If system mode, listen for OS changes
    const handler = (e: MediaQueryListEvent) => {
      if (preference === 'system') {
        setColorMode(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [preference, setColorMode]);

  const selectMode = (mode: ThemeMode) => {
    setPreference(mode);
    localStorage.setItem('theme-preference', mode);
    if (mode === 'system') {
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setColorMode(isDark ? 'dark' : 'light');
    } else {
      setColorMode(mode);
    }
  };

  const isDark = colorMode === 'dark';

  if (variant === 'menu') {
    return (
      <Menu gutter={8}>
        <MenuButton
          as={IconButton}
          aria-label="Toggle color theme"
          icon={
            preference === 'system' ? (
              <Icon as={FiMonitor as any} />
            ) : preference === 'dark' ? (
              <Icon as={FiMoon as any} />
            ) : (
              <Icon as={FiSun as any} />
            )
          }
          size={size}
          variant="ghost"
          borderRadius="full"
          color={isDark ? 'gray.200' : 'gray.700'}
          _hover={{
            bg: isDark ? 'whiteAlpha.150' : 'blackAlpha.050',
          }}
        />
        <MenuList
          bg={isDark ? '#0D1527' : 'white'}
          borderColor={isDark ? 'whiteAlpha.200' : 'gray.200'}
          borderRadius="12px"
          boxShadow="xl"
          p={1}
          minW="140px"
          zIndex={1100}
        >
          <MenuItem
            icon={<Icon as={FiSun as any} />}
            onClick={() => selectMode('light')}
            borderRadius="8px"
            fontSize="13px"
            fontWeight={preference === 'light' ? 600 : 400}
            bg={preference === 'light' ? (isDark ? 'whiteAlpha.100' : 'blue.50') : 'transparent'}
            color={preference === 'light' ? 'brand.500' : (isDark ? 'gray.200' : 'gray.800')}
          >
            <HStack justify="space-between" w="full">
              <Text>Light</Text>
              {preference === 'light' && <Icon as={FiCheck as any} color="brand.500" />}
            </HStack>
          </MenuItem>
          <MenuItem
            icon={<Icon as={FiMoon as any} />}
            onClick={() => selectMode('dark')}
            borderRadius="8px"
            fontSize="13px"
            fontWeight={preference === 'dark' ? 600 : 400}
            bg={preference === 'dark' ? (isDark ? 'whiteAlpha.100' : 'blue.50') : 'transparent'}
            color={preference === 'dark' ? 'brand.500' : (isDark ? 'gray.200' : 'gray.800')}
          >
            <HStack justify="space-between" w="full">
              <Text>Dark</Text>
              {preference === 'dark' && <Icon as={FiCheck as any} color="brand.500" />}
            </HStack>
          </MenuItem>
          <MenuItem
            icon={<Icon as={FiMonitor as any} />}
            onClick={() => selectMode('system')}
            borderRadius="8px"
            fontSize="13px"
            fontWeight={preference === 'system' ? 600 : 400}
            bg={preference === 'system' ? (isDark ? 'whiteAlpha.100' : 'blue.50') : 'transparent'}
            color={preference === 'system' ? 'brand.500' : (isDark ? 'gray.200' : 'gray.800')}
          >
            <HStack justify="space-between" w="full">
              <Text>System</Text>
              {preference === 'system' && <Icon as={FiCheck as any} color="brand.500" />}
            </HStack>
          </MenuItem>
        </MenuList>
      </Menu>
    );
  }

  // Segmented control (default)
  return (
    <HStack
      spacing={1}
      p="3px"
      bg={isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(13, 31, 76, 0.05)'}
      borderRadius="full"
      border="1px solid"
      borderColor={isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(13, 31, 76, 0.08)'}
      backdropFilter="blur(8px)"
      transition="all 0.2s"
    >
      <Tooltip label="Light Mode" placement="bottom" hasArrow openDelay={400}>
        <IconButton
          aria-label="Light mode"
          icon={<Icon as={FiSun as any} boxSize={size === 'sm' ? '13px' : '15px'} />}
          size={size}
          h={size === 'sm' ? '26px' : '32px'}
          w={size === 'sm' ? '28px' : '34px'}
          minW="unset"
          borderRadius="full"
          variant="ghost"
          bg={
            preference === 'light'
              ? isDark
                ? 'whiteAlpha.300'
                : 'white'
              : 'transparent'
          }
          color={
            preference === 'light'
              ? isDark
                ? '#FFB703'
                : '#0D1F4C'
              : isDark
              ? 'gray.400'
              : 'gray.500'
          }
          boxShadow={preference === 'light' ? 'sm' : 'none'}
          _hover={{
            bg: preference === 'light' ? (isDark ? 'whiteAlpha.300' : 'white') : (isDark ? 'whiteAlpha.100' : 'blackAlpha.050'),
            color: isDark ? 'white' : '#0D1F4C',
          }}
          onClick={() => selectMode('light')}
        />
      </Tooltip>

      <Tooltip label="Dark Mode" placement="bottom" hasArrow openDelay={400}>
        <IconButton
          aria-label="Dark mode"
          icon={<Icon as={FiMoon as any} boxSize={size === 'sm' ? '13px' : '15px'} />}
          size={size}
          h={size === 'sm' ? '26px' : '32px'}
          w={size === 'sm' ? '28px' : '34px'}
          minW="unset"
          borderRadius="full"
          variant="ghost"
          bg={
            preference === 'dark'
              ? isDark
                ? 'brand.500'
                : '#0D1F4C'
              : 'transparent'
          }
          color={
            preference === 'dark'
              ? 'white'
              : isDark
              ? 'gray.400'
              : 'gray.500'
          }
          boxShadow={preference === 'dark' ? 'sm' : 'none'}
          _hover={{
            bg: preference === 'dark' ? (isDark ? 'brand.500' : '#0D1F4C') : (isDark ? 'whiteAlpha.100' : 'blackAlpha.050'),
            color: isDark ? 'white' : '#0D1F4C',
          }}
          onClick={() => selectMode('dark')}
        />
      </Tooltip>

      <Tooltip label="System Preference" placement="bottom" hasArrow openDelay={400}>
        <IconButton
          aria-label="System mode"
          icon={<Icon as={FiMonitor as any} boxSize={size === 'sm' ? '13px' : '15px'} />}
          size={size}
          h={size === 'sm' ? '26px' : '32px'}
          w={size === 'sm' ? '28px' : '34px'}
          minW="unset"
          borderRadius="full"
          variant="ghost"
          bg={
            preference === 'system'
              ? isDark
                ? 'whiteAlpha.250'
                : 'white'
              : 'transparent'
          }
          color={
            preference === 'system'
              ? isDark
                ? '#00B4D8'
                : '#1264E8'
              : isDark
              ? 'gray.400'
              : 'gray.500'
          }
          boxShadow={preference === 'system' ? 'sm' : 'none'}
          _hover={{
            bg: preference === 'system' ? (isDark ? 'whiteAlpha.250' : 'white') : (isDark ? 'whiteAlpha.100' : 'blackAlpha.050'),
            color: isDark ? 'white' : '#0D1F4C',
          }}
          onClick={() => selectMode('system')}
        />
      </Tooltip>
    </HStack>
  );
};

export default ThemeToggle;
